/**
 * AI-powered commands for chat and code review functionality.
 * These commands interact with the Anthropic Claude API.
 *
 * @module terminal/commands/ai
 */
import type { CommandResult, CommandContext } from './types';

/**
 * AI commands for intelligent assistance.
 * Provides chat functionality and code review powered by Claude.
 *
 * @example
 * ```
 * ask <question>  - Ask Claude a question about Ameta or general topics
 * review <code>   - Get AI-powered code review (uses Claude Opus 4.5)
 * ```
 *
 * Note: Requires ANTHROPIC_API_KEY environment variable to be configured.
 * Without it, commands will return an error message.
 */
export const aiCommands: Record<
	string,
	(args: string[], context: CommandContext) => CommandResult | null
> = {
	ask: (args) => {
		const message = args.join(' ');
		if (!message) {
			return {
				type: 'text',
				lines: ['Usage: ask <question>', '', 'Example: ask what does ameta do']
			};
		}
		return { type: 'chat', lines: [message] };
	},

	review: (args) => {
		if (!args.length) {
			return {
				type: 'text',
				lines: ['Usage: review <code>', '', 'Paste code to get an AI-powered code review.']
			};
		}
		return { type: 'review', lines: [args.join(' ')] };
	}
};
